import { getCoinSupporters } from "@/lib/api/coins"
import { SupportersResp } from "@/types/coins"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { parseEther } from "viem"

const QUERY_KEYS = {
  ALL: "allSupporters",
  ALL_AVATAR: "allAvatarSupporters",
  LIKED: "likedSupporters",
  LOVED: "lovedSupporters",
  BELIEVER: "believerSupporters",
  LIKED_AVATAR: "likedAvatarSupporters",
  LOVED_AVATAR: "lovedAvatarSupporters",
  BELIEVER_AVATAR: "believerAvatarSupporters",
}

type Props = {
  coinId: string
  coinsPerUSD: number
}

export function useTierSupporters({ coinId, coinsPerUSD }: Props) {
  const { data: liked } = useQuery({
    queryKey: [QUERY_KEYS.LIKED, coinId, coinsPerUSD],
    queryFn: async () =>
      getCoinSupporters({
        coinId,
        maxHoldings: parseEther((5 * coinsPerUSD).toString()),
      }),
  })
  const { data: loved } = useQuery({
    queryKey: [QUERY_KEYS.LOVED, coinId, coinsPerUSD],
    queryFn: async () =>
      getCoinSupporters({
        coinId,
        minHoldings: parseEther((5 * coinsPerUSD).toString()),
        maxHoldings: parseEther((10 * coinsPerUSD).toString()),
      }),
  })
  const { data: believer } = useQuery({
    queryKey: [QUERY_KEYS.BELIEVER, coinId, coinsPerUSD],
    queryFn: async () =>
      getCoinSupporters({
        coinId,
        minHoldings: parseEther((10 * coinsPerUSD).toString()),
      }),
  })
  const { data: likedAvatar } = useQuery({
    queryKey: [QUERY_KEYS.LIKED_AVATAR, coinId, coinsPerUSD],
    queryFn: async () =>
      getCoinSupporters({
        coinId,
        maxHoldings: parseEther((5 * coinsPerUSD).toString()),
        onlyAvatars: true,
      }),
  })
  const { data: lovedAvatar } = useQuery({
    queryKey: [QUERY_KEYS.LOVED_AVATAR, coinId, coinsPerUSD],
    queryFn: async () =>
      getCoinSupporters({
        coinId,
        minHoldings: parseEther((5 * coinsPerUSD).toString()),
        maxHoldings: parseEther((10 * coinsPerUSD).toString()),
        onlyAvatars: true,
      }),
  })
  const { data: believerAvatar } = useQuery({
    queryKey: [QUERY_KEYS.BELIEVER_AVATAR, coinId, coinsPerUSD],
    queryFn: async () =>
      getCoinSupporters({
        coinId,
        minHoldings: parseEther((10 * coinsPerUSD).toString()),
        onlyAvatars: true,
      }),
  })

  return {
    liked,
    loved,
    believer,
    likedAvatar,
    lovedAvatar,
    believerAvatar,
  }
}

export function useAllSupporters({
  initialData,
  coinId,
}: {
  initialData: SupportersResp | undefined
  coinId: string
}) {
  const { data: allSupporters } = useQuery({
    queryKey: [QUERY_KEYS.ALL, coinId],
    queryFn: async () => getCoinSupporters({ coinId }),
    initialData: initialData,
  })

  return {
    allSupporters,
  }
}

export function useAllAvatarSupporters({
  initialData,
  coinId,
}: {
  initialData: SupportersResp | undefined
  coinId: string
}) {
  const { data: allAvatarSupporters } = useQuery({
    queryKey: [QUERY_KEYS.ALL_AVATAR, coinId],
    queryFn: async () =>
      getCoinSupporters({ coinId, onlyAvatars: true, limit: 3 }),
    initialData: initialData,
  })

  return {
    allAvatarSupporters,
  }
}

export function useInvalidateSupporters() {
  const queryClient = useQueryClient()

  async function invalidateTiers() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LIKED] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LOVED] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BELIEVER] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LIKED_AVATAR] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LOVED_AVATAR] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BELIEVER_AVATAR] }),
    ])
  }

  async function invalidateAll() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALL] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALL_AVATAR] }),
    ])
  }

  return { invalidateTiers, invalidateAll }
}
